/* eslint-disable max-len */
// TODO pull all common types and move them to types.d.ts file
import { AxiosResponse } from 'axios';
import {
    UseMutateAsyncFunction,
    useMutation,
    UseMutationOptions,
    useQuery,
    UseQueryOptions,
    UseQueryResult,
} from 'react-query';
import ROUTES from 'src/lib/routes';
import axiosInstance from 'src/lib/api-client/custom-axios';
import { CACHE_ONLY, NO_REFETCH } from '../lib/utils/react-query-utils';
import {
    rewardsGetFavoritesOffersURL,
    rewardsManageFavoriteOffersURL,
    rewardsGetLoyaltyOffersURL,
    rewardsPostLoyaltyAccountURL,
    pointHistoryURL,
    rewardsEnrollURL,
    loyaltyRecyclingOfferURL,
    rewardsGetFeaturedLoyaltyOffersURL,
    rewardsActivateLoyaltyOffersURL,
    rewardsGetCouponDataURL,
} from './urls';
import queryClient from './queryClient';
import { CouponResponse } from './types';

export type FavoriteOffersList = {
    id: string;
    name: string;
    startdate: Date | null;
    enddate: Date | null;
    digitaldescription: string | null;
    digitalviewimage: string | null;
    subscribed: boolean;
    subscriptionid: string | null;
    recommended: boolean;
    offerdisclaimer: string | null;
    alternativedescription: string | null;
    imagepng: string | null;
    includedlist: string | null;
    subscriptionenddate: string | null;
}[];
export interface FavoriteOffers {
    favorites: FavoriteOffersList;
    status: string;
    message: string;
    issuccess: boolean;
}
type GetFavoriteOffersConfig =
    | Omit<
          UseQueryOptions<
              FavoriteOffersList,
              unknown,
              FavoriteOffersList,
              string[]
          >,
          'queryKey' | 'queryFn'
      >
    | undefined;
export function useFavoriteOffersQuery(
    config?: GetFavoriteOffersConfig
): UseQueryResult<FavoriteOffersList> {
    const queryFunc = async (): Promise<FavoriteOffersList> =>
        (await axiosInstance.get<FavoriteOffers>(rewardsGetFavoritesOffersURL))
            .data?.favorites;
    return useQuery(['useFavoriteOffersQuery'], queryFunc, config);
}

export interface LoyaltyOffer {
    name: string;
    offerdescription: string;
    offerdisclaimer: string;
    imagepng: string | null;
    iconname: string;
    destinationUrl: string;
    mode: string;
    type: string;
    channel: string;
    enddate: string;
    digitaldescription: string | null;
    digitalviewimage: string;
    activated: boolean;
    offertype: string;
    offerid: string;
    isfeatured: boolean;
    multiuse: boolean;
    continuitytransactiondesc: string;
    continuitytransactiontotal: string;
    countinuitytransactioncurrent: string;
    ranking: number;
}
export type LoyaltyOfferResponse = {
    offset: number;
    limit: number;
    total: number;
    offers: LoyaltyOffer[];
};
interface LoyaltyOffersQueryParams {
    offerType?:
        | 'MASS'
        | 'TARGETED'
        | 'RECYCLING'
        | 'EVERGREEN-RECYCLING'
        | 'MASS/TARGETED'
        | 'MASS/TARGETED/RECYCLING'
        | 'MASS/TARGETED/RECYCLING/EVERGREEN-RECYCLING';
    config?:
        | Omit<
              UseQueryOptions<
                  LoyaltyOfferResponse,
                  unknown,
                  LoyaltyOfferResponse,
                  any[]
              >,
              'queryKey' | 'queryFn'
          >
        | undefined;
}
export const LoyaltyOffersQueryKey = 'useLoyaltyOffersQuery';
export function useLoyaltyOffersQuery({
    offerType,
    config,
}: LoyaltyOffersQueryParams): UseQueryResult<LoyaltyOfferResponse> {
    const queryFunc = async (): Promise<LoyaltyOfferResponse> =>
        (
            await axiosInstance.get(
                `${rewardsGetLoyaltyOffersURL}?offerType=${offerType}/RECYCLING`
            )
        ).data;
    return useQuery([LoyaltyOffersQueryKey, offerType], queryFunc, config);
}

export function useFeaturedLoyaltyOffersQuery({
    config,
}: LoyaltyOffersQueryParams): UseQueryResult<LoyaltyOfferResponse> {
    const queryFunc = async (): Promise<LoyaltyOfferResponse> =>
        (await axiosInstance.get(`${rewardsGetFeaturedLoyaltyOffersURL}`)).data;
    return useQuery([LoyaltyOffersQueryKey, 'FEATURED'], queryFunc, config);
}

interface UpdateLoyaltyOfferCacheParams {
    offerType: 'MASS' | 'TARGETED' | 'FEATURED';
    updater:
        | LoyaltyOfferResponse
        | ((oldData: LoyaltyOfferResponse | undefined) => LoyaltyOfferResponse);
}
export function updateLoyaltyOfferCache({
    offerType,
    updater,
}: UpdateLoyaltyOfferCacheParams) {
    queryClient.setQueryData(['useLoyaltyOffersQuery', offerType], updater);
}

type FavoriteOffersMutationConfig =
    | Omit<
          UseMutationOptions<
              AxiosResponse<any>,
              unknown,
              { subscribeIdList: string[]; unsubscribeIdList: string[] },
              unknown
          >,
          'mutationFn'
      >
    | undefined;
export function useFavoriteOffersMutation(
    config?: FavoriteOffersMutationConfig
) {
    const mutationFunc = ({
        subscribeIdList,
        unsubscribeIdList,
    }: {
        subscribeIdList: string[];
        unsubscribeIdList: string[];
    }) =>
        axiosInstance.post(rewardsManageFavoriteOffersURL, {
            SubscribeFavoriteOfferIds: subscribeIdList,
            UnSubscribeFavoriteOfferIds: unsubscribeIdList,
        });
    return useMutation(mutationFunc, config);
}

export interface LoyaltyAccountDetailsResponse {
    accounts: [
        {
            lastearnpoints?: number;
            rewardsnumber: number | null;
            rewardsnumberstr: string | null;
            rewardstiercode: string;
            rewardstierdescription: string;
            status: string;
            membersince: string;
            contactinfo: {
                firstname: string | null;
                lastname: string | null;
                address: {
                    addid: string | null;
                    address1: string | null;
                    address2: null;
                    city: string | null;
                    countrycode: string | null;
                    statecode: string | null;
                    zipcode: string | null;
                };
                phonenumber: number | null;
                emailid: string | null;
            };
            termsandconditionaccepted: 'Y' | 'N';
            expirationDate: string | null;
            expired: boolean;
            walletinfo: {
                status: string | null;
                state: string | null;
                walletfetchstatus: string | null;
                balances: {
                    current: number;
                    usable: number;
                    locked: number;
                    lifetime: number;
                    lifetimespend: number;
                    lifetimespendvalue: number;
                    available: number;
                    refundable: number;
                    usablevalue: number;
                };
            };
        }
    ];
}

export const useLoyaltyAccountDetailsQueryKey = 'useLoyaltyAccountDetailsQuery';
type UseLoyaltyAccountDetailsQuery =
    | Omit<
          UseQueryOptions<
              LoyaltyAccountDetailsResponse,
              unknown,
              LoyaltyAccountDetailsResponse,
              (string | undefined | null)[]
          >,
          'queryKey' | 'queryFn'
      >
    | undefined;
export function useLoyaltyAccountDetailsQuery({
    rewardsNumber,
    config,
}: {
    rewardsNumber?: string | undefined | null;
    config?: UseLoyaltyAccountDetailsQuery;
}): UseQueryResult<LoyaltyAccountDetailsResponse> {
    const queryFunc = async (): Promise<LoyaltyAccountDetailsResponse> =>
        (
            await axiosInstance.post(rewardsPostLoyaltyAccountURL, {
                identifierType: 'REWARDS',
                accountIdentifier: rewardsNumber,
            })
        ).data;
    return useQuery(
        [useLoyaltyAccountDetailsQueryKey, rewardsNumber],
        queryFunc,
        config
    );
}

interface EnrollInRewardsRequestBody {
    FirstName: string;
    LastName: string;
    PhoneNumber: string;
    EmailId: string;
}

type EnrollInRewardsConfig =
    | Omit<
          UseMutationOptions<
              AxiosResponse<any>,
              unknown,
              EnrollInRewardsRequestBody,
              unknown
          >,
          'mutationFn'
      >
    | undefined;

export function useEnrollInRewardsMutation(config?: EnrollInRewardsConfig) {
    const mutationFunc = (userData: EnrollInRewardsRequestBody) =>
        axiosInstance.post<any>(rewardsEnrollURL, userData);
    return useMutation(mutationFunc, config);
}

export type PointHistory = [
    {
        trasactioncount: number;
        totalpoints: number;
    }
];
type PointHistoryConfig =
    | Omit<
          UseQueryOptions<PointHistory, unknown, PointHistory, string[]>,
          'queryKey' | 'queryFn'
      >
    | undefined;
export function usePointHistoryQuery({
    rewardsNumber,
    config,
}: {
    rewardsNumber?: string | undefined | null;
    config?: PointHistoryConfig;
    data?: string;
}): UseQueryResult<PointHistory> {
    console.log({ data: window.location.pathname });
    const queryFunc = async (): Promise<PointHistory> =>
        (await axiosInstance.get<PointHistory>(pointHistoryURL)).data;
    return useQuery(['usePointHistoryQuery'], queryFunc, config);
}

type LoyaltyRewardPointsConfig =
    | Omit<
          UseQueryOptions<RewardPoints, unknown, RewardPoints, string[]>,
          'queryFn'
      >
    | undefined;

export type RewardPoints = [
    {
        results: [
            {
                maxnumberofcartridgesrecycled: string;
                points: string;
            }
        ];
    }
];
export function useLoyaltyRewardPointsQuery(
    config?: LoyaltyRewardPointsConfig
) {
    const queryFunc = async (): Promise<RewardPoints> =>
        (await axiosInstance.get<RewardPoints>(loyaltyRecyclingOfferURL)).data;
    return useQuery(['useLoyaltyRewardPointsQuery'], queryFunc, config);
}

export interface ActivateOfferResponse {
    issuccess: boolean;
    limit: number;
    message: string;
    offers: LoyaltyOffer[];
    offset: number;
    status: string;
    total: 64;
}
export type ActivateOfferRequestBody = string[];
type ActivateOfferConfig =
    | Omit<
          UseMutationOptions<
              AxiosResponse<any>,
              unknown,
              ActivateOfferRequestBody,
              unknown
          >,
          'mutationFn'
      >
    | undefined;
export type ActivateOfferMutateAsyncFunc = UseMutateAsyncFunction<
    AxiosResponse<any>,
    unknown,
    ActivateOfferRequestBody,
    unknown
>;
export function useActivateOfferMutation(config?: ActivateOfferConfig) {
    const mutationFunc = (offerIds: ActivateOfferRequestBody) =>
        axiosInstance.post<ActivateOfferResponse>(
            rewardsActivateLoyaltyOffersURL,
            {
                CampaignIds: [...offerIds],
            }
        );
    return useMutation(mutationFunc, {
        // onSuccess: (data) => {
        //     queryClient.setQueryData();
        // },
        ...config,
    });
}

export const getCouponDetails = async (url: string): Promise<any> => {
    try {
        const dataRes: any = await axiosInstance.get(url);
        return dataRes?.data;
    } catch (e: any) {
        return { error: e };
    }
};

// export const LoyaltyOffersQueryKey = 'useLoyaltyOffersQuery';
// export function useLoyaltyOffersQuery({
//     offerType,
//     config,
// }: LoyaltyOffersQueryParams): UseQueryResult<LoyaltyOfferResponse> {
//     const queryFunc = async (): Promise<LoyaltyOfferResponse> =>
//         (
//             await axiosInstance.get(
//                 `${rewardsGetLoyaltyOffersURL}?offerType=${offerType}/RECYCLING`
//             )
//         ).data;
//     return useQuery([LoyaltyOffersQueryKey, offerType], queryFunc, config);
// }

interface GetCouponsParams {
    rewardsNumber?: string;
    config?:
        | Omit<
              UseQueryOptions<
                  CouponResponse,
                  unknown,
                  CouponResponse,
                  (string | undefined)[]
              >,
              'queryKey' | 'queryFn'
          >
        | undefined;
}

export const GET_COUPONS_QUERY_KEY = 'usercoupons';
export function getCoupons({ rewardsNumber, config }: GetCouponsParams) {
    const queryFunc = async (): Promise<CouponResponse> =>
        (
            await axiosInstance.get<CouponResponse>(
                rewardsGetCouponDataURL.replace(
                    '%rewardsnumber%',
                    rewardsNumber || ''
                )
            )
        ).data;

    return useQuery([GET_COUPONS_QUERY_KEY, rewardsNumber], queryFunc, {
        ...config,
        ...CACHE_ONLY,
        enabled: !!rewardsNumber,
    });
}
